import { api_configuration } from "../../common";
import api_client_request = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.client_request.alan_api;

export class UpdateMutationBuilder {
	constructor(public node: api_client_request.Tnode_update) {}

	public static create(): UpdateMutationBuilder {
		return new UpdateMutationBuilder({
			properties: {},
		});
	}

	public file(name: string, token: string, extension: string): UpdateMutationBuilder {
		this.node.properties[name] = {
			type: [
				"file",
				{
					"new token": token,
					"new extension": extension,
				},
			],
		};
		return this;
	}

	public addCollectionEntry(name: string, key: string): InitializeMutationBuilder {
		const collection_property = this.getCollection(name);
		const entry = collection_property.entries[key];
		let create_entry: { type: ["create", api_client_request.Tcreate] };
		if (entry === undefined || entry.type[0] !== "create") {
			create_entry = {
				type: [
					"create",
					{
						initialization: {
							properties: {},
						},
					},
				],
			};
			collection_property.entries[key] = create_entry;
		} else {
			create_entry = collection_property.entries[key] as typeof create_entry;
		}
		return new InitializeMutationBuilder(create_entry.type[1].initialization);
	}

	public updateCollectionEntry(name: string, key: string): UpdateMutationBuilder {
		const collection_property = this.getCollection(name);
		const entry = collection_property.entries[key];
		let update_entry: { type: ["update", api_client_request.Tupdate__type__entries] };
		if (entry === undefined || entry.type[0] !== "update") {
			update_entry = {
				type: [
					"update",
					{
						update: {
							properties: {},
						},
					},
				],
			};
			collection_property.entries[key] = update_entry;
		} else {
			update_entry = collection_property.entries[key] as typeof update_entry;
		}
		return new UpdateMutationBuilder(update_entry.type[1]["update"]);
	}

	public deleteCollectionEntry(name: string, key: string): UpdateMutationBuilder {
		const collection_property = this.getCollection(name);
		const entry = collection_property.entries[key];
		let remove_entry: { type: ["remove", api_client_request.Tremove] };
		if (entry === undefined || entry.type[0] !== "remove") {
			remove_entry = {
				type: [
					"remove",
					{
						"delete node": {},
					},
				],
			};
			collection_property.entries[key] = remove_entry;
		} else {
			remove_entry = collection_property.entries[key] as typeof remove_entry;
		}
		return this;
	}

	public group(name: string): UpdateMutationBuilder {
		const group_property = this.node.properties[name];
		if (group_property?.type[0] === "group") {
			return new UpdateMutationBuilder(group_property.type[1]["update"]);
		} else {
			const new_group: { type: ["group", api_client_request.Tgroup__type__properties__node_update] } = {
				type: [
					"group",
					{
						update: {
							properties: {},
						},
					},
				],
			};
			this.node.properties[name] = new_group;
			return new UpdateMutationBuilder(new_group.type[1]["update"]);
		}
	}

	public number(name: string, value: number): UpdateMutationBuilder {
		this.node.properties[name] = {
			type: [
				"number",
				{
					"new value": value,
				},
			],
		};
		return this;
	}

	public text(name: string, value: string): UpdateMutationBuilder {
		this.node.properties[name] = {
			type: [
				"text",
				{
					"new value": value,
				},
			],
		};
		return this;
	}

	public update_state(state_group: string, state: string): UpdateMutationBuilder {
		const state_property = this.node.properties[state_group];
		if (
			state_property?.type[0] === "state group" &&
			state_property?.type[1].state === state &&
			state_property?.type[1].type[0] === "update"
		) {
			return new UpdateMutationBuilder(state_property.type[1].type[1]["update"]);
		} else {
			const update_node = {
				properties: {},
			};
			this.node.properties[state_group] = {
				type: [
					"state group",
					{
						state: state,
						type: [
							"update",
							{
								update: update_node,
							},
						],
					},
				],
			};
			return new UpdateMutationBuilder(update_node);
		}
	}

	public set_state(state_group: string, state: string): InitializeMutationBuilder {
		const state_property = this.node.properties[state_group];
		if (
			state_property?.type[0] === "state group" &&
			state_property?.type[1].state === state &&
			state_property?.type[1].type[0] === "set"
		) {
			return new InitializeMutationBuilder(state_property.type[1].type[1]["initialization"]);
		} else {
			const init_node = {
				properties: {},
			};
			this.node.properties[state_group] = {
				type: [
					"state group",
					{
						state: state,
						type: [
							"set",
							{
								initialization: init_node,
							},
						],
					},
				],
			};
			return new InitializeMutationBuilder(init_node);
		}
	}

	public mergeWith(other: UpdateMutationBuilder): UpdateMutationBuilder {
		for (const [name, property] of Object.entries(other.node.properties)) {
			switch (property.type[0]) {
				case "text":
					this.text(name, property.type[1]["new value"]);
					break;
				case "number":
					this.number(name, property.type[1]["new value"]);
					break;
				case "file":
					this.file(name, property.type[1]["new token"], property.type[1]["new extension"]);
					break;
				case "group":
					this.group(name).mergeWith(new UpdateMutationBuilder(property.type[1].update));
					break;
				case "state group": {
					const stategroup = property.type[1];
					switch (stategroup.type[0]) {
						case "set":
							this.set_state(name, stategroup.state);
							break;
						case "update":
							this.update_state(name, stategroup.state).mergeWith(new UpdateMutationBuilder(stategroup.type[1].update));
							break;
					}
					break;
				}
				case "collection": {
					const collection = other.getCollection(name);
					for (const [key, entry] of Object.entries(collection.entries)) {
						if (entry.type === "remove") {
							this.deleteCollectionEntry(name, key);
							continue;
						}
						switch (entry.type[0]) {
							case "create":
								this.addCollectionEntry(name, key);
								break;
							case "remove":
								this.deleteCollectionEntry(name, key);
								break;
							case "update":
								this.updateCollectionEntry(name, key).mergeWith(new UpdateMutationBuilder(entry.type[1].update));
								break;
						}
					}
					break;
				}
			}
		}
		return this;
	}

	private getCollection(name: string): api_client_request.Tcollection__type__properties__node_update {
		const collection = this.node.properties[name];
		let collection_type: { type: ["collection", api_client_request.Tcollection__type__properties__node_update] };
		if (collection === undefined || collection.type[0] !== "collection") {
			collection_type = {
				type: [
					"collection",
					{
						entries: {},
					},
				],
			};
			this.node.properties[name] = collection_type;
		} else {
			collection_type = this.node.properties[name] as typeof collection_type;
		}
		return collection_type.type[1];
	}
}

export class InitializeMutationBuilder {
	constructor(public node: api_client_request.Tnode_initialization) {}

	public static create(): InitializeMutationBuilder {
		return new InitializeMutationBuilder({
			properties: {},
		});
	}

	public state(state_group: string, state: string): InitializeMutationBuilder {
		const state_group_property = this.node.properties[state_group];
		if (state_group_property?.type[0] === "state group" && state_group_property?.type[1].state === state) {
			return new InitializeMutationBuilder(state_group_property.type[1].initialization);
		} else {
			const set_state: NodeInitialization<"state group"> = {
				type: [
					"state group",
					{
						state: state,
						initialization: {
							properties: {},
						},
					},
				],
			};
			this.node.properties[state_group] = set_state;
			return new InitializeMutationBuilder(set_state.type[1]["initialization"]);
		}
	}

	public collection(name: string): InitializeMutationBuilder {
		this.getCollection(name);
		return this;
	}

	public collectionEntry(name: string, key: string): InitializeMutationBuilder {
		const collection = this.getCollection(name);
		if (collection.entries[key] === undefined) {
			collection.entries[key] = {
				initialization: {
					properties: {},
				},
			};
		}
		return new InitializeMutationBuilder(collection.entries[key].initialization);
	}

	public group(name: string): InitializeMutationBuilder {
		const group_property = this.node.properties[name];
		if (group_property?.type[0] === "group") {
			return new InitializeMutationBuilder(group_property.type[1]["initialization"]);
		} else {
			const new_group: NodeInitialization<"group"> = {
				type: [
					"group",
					{
						initialization: {
							properties: {},
						},
					},
				],
			};
			this.node.properties[name] = new_group;
			return new InitializeMutationBuilder(new_group.type[1]["initialization"]);
		}
	}

	public number(name: string, value: number): InitializeMutationBuilder {
		this.node.properties[name] = {
			type: [
				"number",
				{
					value: value,
				},
			],
		};
		return this;
	}

	public text(name: string, value: string): InitializeMutationBuilder {
		this.node.properties[name] = {
			type: [
				"text",
				{
					value: value,
				},
			],
		};
		return this;
	}

	public file(name: string, token: string, extension: string): InitializeMutationBuilder {
		this.node.properties[name] = {
			type: [
				"file",
				{
					token: token,
					extension: extension,
				},
			],
		};
		return this;
	}

	private getCollection(name: string): api_client_request.Tcollection__type__properties__node_initialization {
		const collection = this.node.properties[name];
		let collection_type: NodeInitialization<"collection">;
		if (collection === undefined || collection.type[0] !== "collection") {
			collection_type = {
				type: [
					"collection",
					{
						entries: {},
					},
				],
			};
			this.node.properties[name] = collection_type;
		} else {
			collection_type = this.node.properties[name] as typeof collection_type;
		}
		return collection_type.type[1];
	}
}

type NodeInitialization<T extends api_client_request.Tproperties__node_initialization["type"][0]> = {
	type: Extract<api_client_request.Tproperties__node_initialization["type"], [T, any]>;
} & api_client_request.Tproperties__node_initialization;
